/**
 * The formula with its current numbers put in — "= $7,597.61 + $5,000.00
 * (overridden) = $12,597.61".
 *
 * Why it exists: after correcting a component, a user needs to see the
 * correction land. The formula alone says what is being done, the component
 * list says what the parts are, but neither shows the arithmetic AS PERFORMED,
 * which is the thing an override changes.
 *
 * THE UI COMPUTES NOTHING. Every operand is a value the server sent in
 * `inputs[]`, the operators are lifted from the server's own equation string,
 * and the total is the cell's `effective_value` — the server's number,
 * unmodified. A UI that recomputed the total would be running a second
 * implementation of a formula the epic deliberately keeps in Python, and it
 * would disagree with the dashboard the moment either changed.
 *
 * Faithful or absent: if the equation's shape cannot be reproduced exactly —
 * parentheses, an operand that repeats, a count that does not line up with the
 * inputs — this returns null and the caller shows the formula and the component
 * list without a substituted line. A plausible-looking line with the operands
 * in the wrong order would be worse than no line, because it would be read as
 * an explanation.
 */

import type { KpiCellInput, KpiUnit } from "../../types/kpi";
import { formatKpiValueExact, formatUnitlessValue } from "./formatKpiValue";

export interface SubstitutionTerm {
	/** The formatted value, in the INPUT's own unit. */
	text: string;
	/** Operator printed BEFORE this term ("+", "-", "/", "×"); empty for the first. */
	operator: string;
	overridden: boolean;
	metricKey: string;
}

export interface KpiSubstitution {
	terms: SubstitutionTerm[];
	/** The server's value for the cell, formatted. Never computed here. */
	total: string;
}

/** `opex_payroll_admin` and "Administrative Payroll" both reduce to letters. */
function normalize(text: string): string {
	return text.toLowerCase().replace(/[^a-z0-9]/g, "");
}

interface ParsedEquation {
	operands: string[];
	/** One per operand; the first is always "". */
	operators: string[];
}

/**
 * Split the right-hand side into operands and the operators between them.
 *
 * Refuses anything it cannot reproduce literally: parentheses mean precedence
 * this renderer does not model, and without a right-hand side there is nothing
 * to substitute into.
 */
export function parseEquation(formula: string): ParsedEquation | null {
	const split = formula.indexOf("=");
	if (split === -1) return null;
	const rhs = formula.slice(split + 1).trim();
	if (rhs === "" || /[()]/.test(rhs)) return null;

	const parts = rhs.split(/\s+([+\-/*x×])\s+/);
	if (parts.length < 3) return null;

	const operands: string[] = [];
	const operators: string[] = [""];
	parts.forEach((part, index) => {
		if (index % 2 === 0) operands.push(part.trim());
		else operators.push(part === "*" || part === "x" ? "×" : part);
	});
	if (operands.some((operand) => operand === "")) return null;
	return { operands, operators };
}

/**
 * Line up each operand with the input that supplied it.
 *
 * By NAME where the names correspond, because that is checkable. Where they do
 * not — "Administrative Payroll" against `opex_payroll_admin` — it falls back to
 * position, which is sound for one specific reason: both the equation string and
 * the `inputs[]` order come from the same `DerivedFormula` object on the server,
 * whose `inputs` tuple is the order its display string was written in. If that
 * ever stops being true, the count check is the last guard and this returns
 * null rather than mislabelling numbers.
 */
function alignInputs(
	operands: string[],
	inputs: KpiCellInput[],
): KpiCellInput[] | null {
	if (operands.length !== inputs.length) return null;

	const remaining = [...inputs];
	const byName: KpiCellInput[] = [];
	for (const operand of operands) {
		const wanted = normalize(operand);
		const index = remaining.findIndex(
			(input) => normalize(input.metric_key) === wanted,
		);
		if (index === -1) break;
		byName.push(remaining.splice(index, 1)[0]);
	}
	if (byName.length === operands.length) return byName;

	return [...inputs];
}

export function buildKpiSubstitution(
	formula: string | null | undefined,
	inputs: KpiCellInput[],
	total: number | null | undefined,
	unit: KpiUnit,
): KpiSubstitution | null {
	if (!formula || inputs.length === 0) return null;
	if (total === null || total === undefined) return null;

	const parsed = parseEquation(formula);
	if (parsed === null) return null;

	const aligned = alignInputs(parsed.operands, inputs);
	if (aligned === null) return null;
	// A missing operand cannot be drawn as anything honest — not a blank, and
	// certainly not a zero.
	if (aligned.some((input) => input.value === null || input.value === undefined)) {
		return null;
	}

	return {
		terms: aligned.map((input, index) => ({
			text: input.unit
				? formatKpiValueExact(input.value ?? null, input.unit)
				: formatUnitlessValue(input.value ?? null),
			operator: parsed.operators[index] ?? "",
			overridden: Boolean(input.overridden),
			metricKey: input.metric_key,
		})),
		total: formatKpiValueExact(total, unit),
	};
}
